/* eslint-disable brace-style */
/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSelectedEntity, selectCustomFieldsKey, selectFilterError, selectFilterHistory, selectIsFiltering, selectSearchFunction
} from 'store/Filters/selectors';
import { SEARCH_BY_ENTITIES, ENTITIES } from 'store/Filters/entitiesConstants';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
  addFilter, removeFilter, setSelectedEntity, clearFilterError, clearFilters, setSaveEnabled,
  addFilterHistory,
  rebuildFilters,
  setFilterError,
  storeDashboardFilter
} from 'store/Filters/actions';
import { createNameKey, CUSTOM_FIELDS_OPERATORS, SEARCH_BY_FILTER_KEYS } from 'store/Filters/constants';
import { selectLocation } from 'store/Router/selectors';
import { ACTIONS, CATEGORIES } from 'tracking/GA/constants';
import { trackEvent } from 'tracking/GA';
import DropdownOptions from './components/Dropdown';
import { FilterTags, DraftTag, Tag } from './components/FilterTags';
import {
  DropdownContainer, Input, SearchInput,
  TagsContainer, Backdrop, SearchIcon, ClearIcon, SearchButton
} from './styled';
import { filterByInput, useFocus } from './functions';
import DateInput from './components/DateInput';
import { selectTags } from 'store/Tags/selectors';
import { selectRiskScoreFeatureFlag, selectCurrentWorkspace } from 'store/Faraday/selectors';
import { selectCurrentHost } from 'store/Host/selectors';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_TAGS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { parseOptions } from 'Screens/Contextualization/Manage/components/CustomAttributesModal/functions';
import { selectCustomAttributes } from 'store/CustomAttributes/selectors';
import DateRange from './components/DateRange';
import moment from 'moment';
import { selectAgentEntity } from 'store/Agents/selectors';

const QUICK_SEARCH_KEY = '__quickSearch__';

const NUMERIC_FILTER_KEYS = [
  'commands__id',
  'creator_command_id',
  'cvss2_base_score',
  'cvss3_base_score',
  'cvss4_base_score',
  'id',
  'importance',
  'port',
  'risk',
  'service__port',
  'services__port',
  'status_code',
  'vulnerability_count',
  'vuln_count'
];

const CVSS_FILTER_KEYS = ['cvss2_base_score', 'cvss3_base_score', 'cvss4_base_score'];

// Top-level dropdown key entries for the Tags feature (see store/Filters/constants.js:
// SEARCH_BY_FILTER_KEYS.tags__name and SEARCH_BY_FILTER_KEYS.tag_instances), hidden when
// the backend doesn't expose the Tags feature (e.g. community edition).
const TAGS_FILTER_KEY_VALUES = ['tags__name', 'tag_instances__name'];

// Entity name values (see store/Filters/entitiesConstants.js) for the Reports
// screen's own tabs — My Reports, Templates, Styles, Legacy Reports.
const REPORTS_TAB_ENTITIES = ['reports', 'templates', 'styles', 'legacyReports'];

const BasicFilter = ({ leftIcon, entity, maxHeightDropdown, openUpward }) => {
  const intl = useIntl();
  const searchAriaLabel = intl.formatMessage({ id: 'common.components.button.search' });
  const [basicInputValue, setBasicInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [draftTag, setDraftTag] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedOp, setSelectedOp] = useState(null);
  const [selectedVal, setSelectedVal] = useState(null);
  const [inputRef, setInputFocus, setInputBlur] = useFocus();
  const tagList = useSelector(selectTags).map((tag) => createNameKey(tag.name, tag.name));
  const isSelectedKeyTag = get(selectedKey, 'name.value', '') === 'tags__name';
  const tagsContainerRef = useRef(null);
  const lastQuickSearchFilterRef = useRef({});
  const dispatch = useDispatch();
  const location = useSelector(selectLocation);
  const currentWorkspace = useSelector(selectCurrentWorkspace);
  const currentAsset = useSelector(selectCurrentHost);
  const assetId = get(currentAsset, 'id', get(currentAsset, '_id', 0));
  const selectedEntity = useSelector((state) => getSelectedEntity(state, entity));
  const selectedAgentEntity = useSelector(selectAgentEntity);

  const searchFunction = useSelector(selectSearchFunction);
  const isFiltering = useSelector((state) => selectIsFiltering(selectedEntity, state));
  const filterError = useSelector((state) => selectFilterError(selectedEntity, state));
  const filterHistory = useSelector((state) => selectFilterHistory(selectedEntity, state));
  const quickSearchField = get(ENTITIES, `${selectedEntity}.quickSearchField`, '');
  const allowedByLicense = useSelector(selectRiskScoreFeatureFlag);
  const hasTagsFeature = useSelector((state) => selectAllowedByRole(state, FEATURE_TAGS, PERMISSION_TYPES.READ));
  const subKey = get(selectedKey, 'name.value', '');
  const isSubKey =
    subKey.includes('__') &&
    subKey !== 'triggered_by__username' &&
    subKey !== 'host__os' &&
    subKey !== 'tags__name' &&
    subKey !== 'status_history__change_date';
  const isCustomField = selectedKey && (selectedKey.name.value === 'custom_fields->');
  const customFields = useSelector(selectCustomAttributes);
  const customFieldsKeys = useSelector(selectCustomFieldsKey);
  const primaryKeyOperators = get(selectedKey, 'operators', []);
  const primaryKeyAutocomplete = get(selectedKey, 'autocomplete', []);
  const subKeyOperators = get(SEARCH_BY_FILTER_KEYS, `${subKey}.operators`, []);
  const subKeyAutocomplete = get(SEARCH_BY_FILTER_KEYS, `${subKey}.autocomplete`, []);
  const isImpactKey = get(selectedKey, 'name.value', '') === 'impact';
  const impactTypesAdded = filterHistory.filter((f) => f.name === 'impact').map((f) => f.op);
  const impactKeyOperators = primaryKeyOperators.filter((operator) => !impactTypesAdded.includes(operator.name.value));

  const entityFilterKeys = get(ENTITIES, `${selectedEntity}.values`, []);
  const newEntityFilterKeys = selectedEntity.includes('vulns') ? entityFilterKeys.length > 0 ? [...entityFilterKeys, customFieldsKeys] : [] : entityFilterKeys;
  newEntityFilterKeys.sort((a, b) => (a?.name?.label || '').localeCompare(b?.name?.label || ''));
  const filteredEntityKeys = filterByInput(newEntityFilterKeys, basicInputValue);
  const newFilteredEntityKeys = newEntityFilterKeys.filter((i) => i?.name?.value !== 'risk');
  const entityKeysAllowedByLicense = ((selectedEntity === 'vulns') && !allowedByLicense) ? newFilteredEntityKeys : filteredEntityKeys;
  const entityKeysAllowedByTagsFeature = hasTagsFeature
    ? entityKeysAllowedByLicense
    : entityKeysAllowedByLicense.filter((i) => !TAGS_FILTER_KEY_VALUES.includes(i?.name?.value));

  const quickSearchFieldLabel = get(ENTITIES, `${selectedEntity}.quickSearchFieldLabel`, quickSearchField);
  const quickSearchOption = (quickSearchField && basicInputValue.trim())
    ? { ...createNameKey(`Search "${basicInputValue.trim()}" in ${quickSearchFieldLabel}`, QUICK_SEARCH_KEY), wrapText: true }
    : null;
  const entityKeysWithQuickSearch = quickSearchOption ? [quickSearchOption, ...entityKeysAllowedByTagsFeature] : entityKeysAllowedByTagsFeature;

  const getChoiceAutocomplete = () => {
    const customField = customFields.find((cf) => cf.field_name === selectedKey.name.value);
    const options = parseOptions(customField.field_metadata);
    const parsedOptions = options.map((item) => createNameKey(item, item));
    return parsedOptions;
  };

  const filterKeyOperators = selectedKey?.type ? CUSTOM_FIELDS_OPERATORS[selectedKey.type] : isSubKey ? subKeyOperators : isImpactKey ? impactKeyOperators : primaryKeyOperators;

  const customFieldsAutocomplete = isCustomField && get(customFieldsKeys, 'autocomplete', []);

  const filteredKeyOperators = filterByInput(filterKeyOperators, basicInputValue);

  const keysSupportsIsOneOf = ['cve_instances__name', 'target', 'ip', 'hostname', 'tag_instances__name', 'status_history__status', 'cwe__name', 'tools', 'category', 'workspaces'];
  const complexKeys = ['cve_instances__name', 'hostnames__name', 'tag_instances__name', 'cwe__name'];

  const supportsList = () => {
    const opSupportsList = draftTag?.op === 'is one of' || draftTag?.op === 'is not one of';
    return subKey && keysSupportsIsOneOf.includes(subKey) && opSupportsList;
  };

  const autocompleteInputSugestion = () => {
    const isDate = subKey.includes('date') || selectedKey?.type?.includes('date') || subKey.includes('last_run') || subKey.includes('last_detected');
    if (isDate) return '';
    if (supportsList() && !subKey.includes('status_history')) return ([createNameKey('Type one or more items, separated by commas', basicInputValue)]);
    return ([createNameKey(basicInputValue || 'Type your query', basicInputValue)]);
  };

  const returnAutocomplete = () => {
    if (selectedKey?.type === 'choice') return getChoiceAutocomplete();
    if (isSelectedKeyTag) return tagList;
    if (isSubKey && subKey !== 'status_history__status') return subKeyAutocomplete;
    if (isSubKey && subKey === 'status_history__status') {
      const statusesAdded = filterHistory.map((f) => f?.val?.val);
      const options = subKeyAutocomplete.filter((opt) => !statusesAdded.includes(opt.name.value));
      return options;
    }
    return primaryKeyAutocomplete;
  };

  const filterKeyAutocomplete = returnAutocomplete();
  const matchedKeyAutocomplete = filterKeyAutocomplete.filter((option) => (
    option.name.value.toLowerCase().includes(basicInputValue.toLowerCase()) ||
    option.name.label.toLowerCase().includes(basicInputValue.toLowerCase())
  ));
  const useTypedListSuggestion = supportsList() && basicInputValue.includes(',');
  const filteredKeyAutocomplete = useTypedListSuggestion
    ? autocompleteInputSugestion()
    : matchedKeyAutocomplete.length > 0
      ? matchedKeyAutocomplete
      : autocompleteInputSugestion();

  const trackSearch = () => {
    const trackAction = get(ACTIONS, `search.${selectedEntity}.basic`, '');
    if (trackAction) dispatch(trackEvent(CATEGORIES.search, trackAction.name, trackAction.label));
  };

  const commitQuickSearch = (text) => {
    const trimmedValue = text.trim();
    if (!selectedEntity || !trimmedValue || !quickSearchField) return;
    const quickSearchOp = get(ENTITIES, `${selectedEntity}.quickSearchOp`, 'ilike');
    const val = quickSearchOp === 'ilike' ? `%${trimmedValue}%` : trimmedValue;
    const filter = { name: quickSearchField, op: quickSearchOp, val };
    const previousQuickSearchFilter = lastQuickSearchFilterRef.current[selectedEntity];
    // Only remove the exact filter our own previous quick search added — not any
    // manually-built filter that happens to target the same field.
    if (previousQuickSearchFilter) dispatch(removeFilter(selectedEntity, previousQuickSearchFilter));
    dispatch(addFilter(selectedEntity, filter));
    lastQuickSearchFilterRef.current[selectedEntity] = filter;
    setBasicInputValue('');
    setShowDropdown(false);
    setInputBlur();
    dispatch(searchFunction());
    trackSearch();
    dispatch(setSaveEnabled(true));
  };

  const currentLocationEntity = () => {
    if (entity) return ENTITIES[entity];
    else if (location.pathname.includes(`host/${currentWorkspace}/${assetId}/vulns`)) return ENTITIES.vulnsAssets;
    else if (location.pathname.startsWith('/vulns/')) return ENTITIES.vulns;
    else if (location.pathname.includes(`host/${currentWorkspace}/${assetId}/services`)) return ENTITIES.servicesAssets;
    else if (location.pathname.includes('/hosts')) return ENTITIES.assetsGeneral;
    else if (/\/host\/[^/]+\/[^/]+\/services(\/[^/]+)?$/.test(location.pathname)) return ENTITIES.servicesAssets;
    else if (location.pathname.includes('/automation/schedule_cloud')) return ENTITIES.cloudScheduler;
    else if (location.pathname.includes('/report/edit') || location.pathname.includes('/report/new')) return ENTITIES.reports;
    else if (location.pathname.includes('/legacy-report')) return ENTITIES.legacyReports;
    else if (location.pathname.includes('/templates')) return ENTITIES.templates;
    else if (location.pathname.includes('/styles')) return ENTITIES.styles;

    // In the agents section, the logic changes, since it is governed by tabs
    else if (location.pathname.includes('/automation/agents')) {
      return ENTITIES[selectedAgentEntity];
    }

    else return SEARCH_BY_ENTITIES.find((e) => location.pathname.includes(e.pathname));
  };

  const prevEntityRef = useRef(null);

  useEffect(() => {
    const entity = get(currentLocationEntity(), 'name.value', '');
    if (entity) dispatch(setSelectedEntity(entity));
    // The Reports screen (My Reports/Templates/Styles/Legacy Reports) reuses this
    // same, never-unmounted search bar across its tabs, and each tab's applied
    // filters/filterHistory live in Redux keyed by entity — so they're never reset
    // on their own and reappear (as tags) whenever the user comes back to that tab,
    // even though the tab's initial data load no longer applies them. Clearing the
    // tab being left (not just the local draft text) is what actually makes them
    // gone for good. Scoped to only these entities so filters elsewhere in the app
    // (Manage, Assets, etc.) keep persisting across navigation as they always have.
    if (REPORTS_TAB_ENTITIES.includes(prevEntityRef.current) && REPORTS_TAB_ENTITIES.includes(entity) && prevEntityRef.current !== entity) {
      dispatch(clearFilters(prevEntityRef.current));
      setBasicInputValue('');
      setShowDropdown(false);
      resetFilterIndexes();
    }
    prevEntityRef.current = entity;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, assetId, currentWorkspace, selectedAgentEntity]);

  const onSearchGlassClick = () => {
    if (selectedEntity) {
      dispatch(searchFunction());
      trackSearch();
      dispatch(setSaveEnabled(true));
    }
  };

  const resetFilterIndexes = () => {
    setDraftTag({});
    setSelectedKey(null);
    setSelectedOp(null);
    setSelectedVal(null);
  };

  const onDropdownSearch = () => {
    if (searchFunction) dispatch(searchFunction());
    trackSearch();
    setShowDropdown(false);
    dispatch(setSaveEnabled(true));
    setInputBlur();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setBasicInputValue('');
      setShowDropdown(false);
      setInputBlur();
      resetFilterIndexes();
    }
    if (e.key === 'Backspace') {
      const filterHistoryCount = filterHistory.length;

      if (!basicInputValue) {
        if (!isEmpty(draftTag)) resetFilterIndexes();
        else if (filterHistoryCount > 0) dispatch(removeFilter(selectedEntity, filterHistory[filterHistoryCount - 1]));
        else if (filterHistoryCount === 0) {
          dispatch(setSaveEnabled(false));
        }
      }
    }
  };

  const onSelect = (elementName, elementValue) => {
    let filterTag = {};
    if (elementName === 'key') {
      if (elementValue.name.value === QUICK_SEARCH_KEY) {
        commitQuickSearch(basicInputValue);
        return;
      }
      setSelectedKey(elementValue);
      filterTag = { name: elementValue.name.label };
    } else if (elementName === 'val') {
      onValueSelect(elementValue);
    } else {
      setSelectedOp(elementValue);
      filterTag = { ...draftTag, op: elementValue.name.label };
    }

    setBasicInputValue('');
    setInputFocus();
    setDraftTag(filterTag);
  };

  const NO_WILDCARD_KEYS = ['last_execution_tool'];

  const onValueSelect = (selectedVal) => {
    const name = selectedKey.name.value;
    const op = selectedOp.name.value;
    const rawVal = `${selectedVal.name.value}`.trim();
    const type = get(selectedKey, 'type', '');
    const isNumericFilter = NUMERIC_FILTER_KEYS.includes(name);
    const numericVal = Number(rawVal);
    const isInvalidNumericFilter = isNumericFilter && !Number.isFinite(numericVal);
    const isCvssOutOfRange = CVSS_FILTER_KEYS.includes(name) && Number.isFinite(numericVal) && (numericVal < 0 || numericVal > 10);
    const val = (op === 'ilike' && !NO_WILDCARD_KEYS.includes(name)) ? `%${rawVal}%` : isNumericFilter ? numericVal : rawVal;

    let filter = {};

    if (rawVal !== '') {
      if (isInvalidNumericFilter) {
        dispatch(setFilterError(selectedEntity, 'Invalid numeric value. Please use numbers for this filter.'));
        setInputFocus();
        return;
      }
      if (isCvssOutOfRange) {
        dispatch(setFilterError(selectedEntity, 'CVSS value must be between 0 and 10.'));
        setInputFocus();
        return;
      }
      if (filterError) dispatch(clearFilterError(selectedEntity));
      setSelectedVal(selectedVal);

      const isComplexSearch = complexKeys.includes(name);
      const supportsList = (draftTag) && (draftTag.op === 'is one of');
      const stringWithCommas = rawVal.includes(',');
      const parsedValues = stringWithCommas ? rawVal.split(',').map((t) => t.trim()) : [];
      const multipleFilters = parsedValues?.length > 0;
      if (isComplexSearch && supportsList && stringWithCommas && multipleFilters) {
        const values = (name === 'cve_instances__name') ? parsedValues.map((v) => v.toUpperCase()) : parsedValues;
        const filters = [];
        const parentOp = selectedOp.name.label === 'is not one of' ? 'not_any' : primaryKeyOperators[0].name.value;
        values.forEach((v) => {
          const parsedFilter = {
            name: name.split('__')[0],
            op: parentOp,
            val: {
              name: name.split('__')[1],
              op,
              val: v
            }
          };
          filters.push(parsedFilter);
          dispatch(addFilterHistory(selectedEntity, parsedFilter));
        });
        setBasicInputValue('');
        dispatch(rebuildFilters(selectedEntity, filters));
      } else if (supportsList && stringWithCommas && multipleFilters) {
        if (op === 'is_one_of' || op === 'is_not_one_of') {
          const parsedFilter = { name, op, val: parsedValues };
          dispatch(addFilterHistory(selectedEntity, parsedFilter));
          setBasicInputValue('');
          dispatch(rebuildFilters(selectedEntity));
        } else {
          const filters = [];
          parsedValues.forEach((v) => {
            const parsedFilter = {
              name,
              op,
              val: v
            };
            filters.push(parsedFilter);
            dispatch(addFilterHistory(selectedEntity, parsedFilter));
          });
          setBasicInputValue('');
          dispatch(rebuildFilters(selectedEntity, filters));
        }
      } else {
        if (isSubKey && (subKey !== 'status_history__change_date')) {
          if (name === 'triggered_by__username') {
            const filter = {
              name,
              op,
              val
            };

            setBasicInputValue('');
            dispatch(addFilter(selectedEntity, filter));
            return;
          }
          const value = ((name === 'cve_instances__name') || (name === 'cwe__name')) ? val.toUpperCase() : val;
          const complexKeys = ['cve_instances__name', 'hostnames__name', 'cwe__name', 'tag_instances__name', 'status_history__status'];
          const isComplexSearch = complexKeys.includes(name);
          const isNotOneOf = (draftTag) && (draftTag.op === '!=');
          const getOp = () => {
            if (isComplexSearch && isNotOneOf) return primaryKeyOperators[1].name.value;
            else if (isComplexSearch && selectedOp.name.label === 'is not one of') return 'not_any';
            else return primaryKeyOperators[0].name.value;
          };
          filter = {
            name: name.split('__')[0],
            op: getOp(),
            val: {
              name: name.split('__')[1],
              op,
              val: value
            }
          };

          setBasicInputValue('');
          dispatch(addFilter(selectedEntity, filter));
        } else if (op === 'range') {
          const values = val.split('-');
          const parsedDates = values.map((v) => moment(v).format('YYYY-MM-DD')).toString();
          filter = {
            name,
            op,
            val: parsedDates
          };
          setBasicInputValue('');
          dispatch(addFilter(selectedEntity, filter));
        } else {
          filter = {
            name: type ? `custom_fields->${name}` : name,
            op,
            val: (op === 'is_one_of' || op === 'is_not_one_of') ? [val] : val
          };
          setBasicInputValue('');
          dispatch(addFilter(selectedEntity, filter));
        }
      }
      if (selectedKey?.name?.value !== 'status_history__status') {
        resetFilterIndexes();
        setShowDropdown(false);
      }
      if (selectedKey?.name?.value === 'status_history__status') setDraftTag({});

      dispatch(setSaveEnabled(true));
    }
    setInputFocus();
  };

  const clearAllFilters = () => {
    if (selectedEntity) {
      if (filterError) dispatch(clearFilterError(selectedEntity));
      if (isFiltering) dispatch(clearFilters(selectedEntity));
      delete lastQuickSearchFilterRef.current[selectedEntity];
      if (selectedEntity === ENTITIES.vulns.name.value) {
        dispatch(storeDashboardFilter('confirmed', false));
        dispatch(storeDashboardFilter('closed', false));
      }
      dispatch(searchFunction());
      dispatch(setSelectedEntity(''));
    }
    setBasicInputValue('');
    setInputBlur();
    resetFilterIndexes();
  };

  const onBackdropClickHandler = () => {
    setShowDropdown(false);
    resetFilterIndexes();
    setBasicInputValue('');
  };

  const tagsContainerWidth = tagsContainerRef.current ? tagsContainerRef.current.clientWidth : 44;
  const dropdownPosition = (tagsContainerWidth < 415) ? tagsContainerWidth : 415;

  const isVisible = {
    entity: showDropdown && !selectedEntity,
    key: showDropdown && !selectedKey,
    op: showDropdown && selectedKey && !selectedOp,
    val: showDropdown && selectedOp && !selectedVal,
    customFields: showDropdown && selectedKey && !selectedOp && selectedKey.name.value === 'custom_fields->',
    statusHistoryStatus: showDropdown && selectedKey && selectedVal && subKey === 'status_history__status'
  };

  const showDateInput = ((subKey.includes('date') && !subKey.includes('status_history__change_date')) || selectedKey?.type?.includes('date') || subKey.includes('last_run') || subKey.includes('last_detected')) && isVisible.val && selectedOp?.name?.label !== 'range';
  const showDateRangeInput = selectedOp?.name?.label === 'range' && isVisible.val;

  const reportCreateEdit = selectedEntity === 'reportsCreateEdit';

  return (
    <>
      <SearchInput leftIcon={ leftIcon }>
        { leftIcon && <SearchButton onClick={ null } startIcon={ <SearchIcon /> } ariaLabel={ searchAriaLabel } /> }
        { !reportCreateEdit &&
          <TagsContainer ref={ tagsContainerRef } leftIcon={ leftIcon }>
            <FilterTags entity={ entity } onSearch={ () => { if (searchFunction) dispatch(searchFunction()); } } />
            <Tag type="val" value={ selectedKey?.type ? 'Custom Attribute' : '' } />
            <DraftTag draftTag={ draftTag } />
          </TagsContainer>
        }
        { (!showDateInput && !showDateRangeInput) && (<Input
          ref={ inputRef }
          placeholder={ isFiltering ? '' : currentLocationEntity()?.placeholder }
          value={ basicInputValue }
          onChange={ (e) => setBasicInputValue(e.target.value) }
          onFocus={ () => setShowDropdown(true) }
          onKeyDown={ onKeyDown }
        />)}
        { (showDateInput && !showDateRangeInput) && <DateInput onValueSelect={ (val) => onSelect('val', createNameKey(val, val)) } />}
        { showDateRangeInput && <DateRange onValueSelect={ (val) => onSelect('val', createNameKey(val, val)) } />}
      </SearchInput>
      <DropdownContainer x={ dropdownPosition } openUpward={ openUpward }>
        { isVisible.key && (
          <DropdownOptions
            key={ quickSearchOption ? 'quick-search' : 'plain' }
            onSearch={ onDropdownSearch }
            options={ entityKeysWithQuickSearch }
            onSelect={ (val) => onSelect('key', val) }
            maxHeightDropdown={ maxHeightDropdown }
            defaultSelect={ !!quickSearchOption }
          />
        ) }
        { isVisible.customFields && <DropdownOptions onSearch={ onDropdownSearch } options={ customFieldsAutocomplete } onSelect={ (val) => onSelect('key', val) } maxHeightDropdown={ maxHeightDropdown } /> }
        { isVisible.op && <DropdownOptions defaultSelect onSearch={ onDropdownSearch } options={ filteredKeyOperators } onSelect={ (val) => onSelect('op', val) } maxHeightDropdown={ maxHeightDropdown } /> }
        { isVisible.val && <DropdownOptions isLastDropdown={ !subKey.includes('status_history') } defaultSelect onSearch={ onDropdownSearch } options={ filteredKeyAutocomplete } onSelect={ onValueSelect } supportsList={ supportsList() && !subKey.includes('status_history') } /> }
        { isVisible.statusHistoryStatus && <DropdownOptions defaultSelect onSearch={ onDropdownSearch } options={ filteredKeyAutocomplete } onSelect={ onValueSelect } supportsList={ supportsList() && !subKey.includes('status_history') } /> }
      </DropdownContainer>
      { selectedKey && <ClearIcon onClick={ clearAllFilters } /> }
      { showDropdown && <Backdrop onClick={ onBackdropClickHandler } /> }
      {!leftIcon && <SearchButton ariaLabel={ searchAriaLabel } onClick={ onSearchGlassClick } startIcon={ <SearchIcon /> } />}
    </>
  );
};

export default BasicFilter;
